import { useEffect, useState, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button, KnackLogo } from '@knack/asterisk-react';
import Cookies from 'js-cookie';

import { type Credentials } from '@/types/auth';
import { useSignUpMutation } from '@/hooks/api/mutations/useSignUpMutation';
import { useValidateSignUpMutation } from '@/hooks/api/mutations/useValidateSignUpMutation';
import { pushToDataLayer, shouldSendAnalytics } from '@/utils/analytics';
import { MARKETING_PAGE_URL } from '@/utils/constants';
import { getGAnalyticsProperties, getReferral } from '@/utils/querystrings';
import { cn } from '@/utils/tailwind';
import { Questionnaire } from '@/pages/sign-up/onboarding/Questionnaire';
import { type QuestionnaireData } from '@/pages/sign-up/onboarding/types';
import { SignUpForm } from '@/pages/sign-up/SignUpForm';
import { SignUpRightSection } from '@/pages/sign-up/SignUpRightSection';
import { TermsAndPrivacy } from '@/pages/sign-up/TermsAndPrivacy';

interface SignUpContainerProps {
  children: ReactNode;
}

function SignUpMain({ children, className }: SignUpContainerProps & { className?: string }) {
  return <main className={cn('flex min-h-screen', className)}>{children}</main>;
}

function SignUpSection({ children }: SignUpContainerProps) {
  return (
    <section className="mb-20 mt-24 flex-auto items-center justify-center md:mb-0 md:mt-0 md:flex">
      {children}
    </section>
  );
}

function SignUpSectionContainer({ children }: SignUpContainerProps) {
  return (
    <div className="container mb-6 flex max-w-6xl flex-col-reverse gap-8 md:flex-row md:gap-20">
      {children}
    </div>
  );
}

function SignUpFormContainer({ children }: SignUpContainerProps) {
  return (
    <div className="mx-auto flex w-full max-w-[500px] flex-col justify-center sm:text-center md:text-left">
      {children}
    </div>
  );
}

function SignUpHeader() {
  return (
    <div className="absolute z-10 flex w-full justify-center pb-12 pt-5 text-brand md:relative md:justify-start md:pl-4 md:pt-10 xl:pl-15">
      <KnackLogo />
    </div>
  );
}

function SignUpFormIntro() {
  const [t] = useTranslation();

  return (
    <h1 className="mb-10 text-3xl" data-testid="auth-signup-header-text">
      {t('components.sign_up.title')}
    </h1>
  );
}

interface SignUpProps {
  canContinueGoogle: boolean;
  onSuccessfulGoogleValidation: (userExists: boolean) => void;
  questionnaireData: QuestionnaireData | undefined;
  onSubmit: (data: Credentials) => void;
  signUpError: string;
  isLoading: boolean;
  enableQuestionnaire: boolean;
  displayQuestionnaire: boolean;
  onQuestionnaireSubmit: (data: QuestionnaireData) => void;
}

function SignUpEmbedded({
  canContinueGoogle,
  onSuccessfulGoogleValidation,
  questionnaireData,
  onSubmit,
  signUpError,
  isLoading,
  enableQuestionnaire,
  displayQuestionnaire,
  onQuestionnaireSubmit
}: SignUpProps) {
  const sendHeightUpdate = () => {
    const height = document.documentElement.scrollHeight;
    window.parent.postMessage(
      {
        type: 'iframeResize',
        height
      },
      MARKETING_PAGE_URL
    );
  };

  useEffect(() => {
    sendHeightUpdate();

    window.addEventListener('resize', sendHeightUpdate);

    const observer = new MutationObserver(sendHeightUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });

    return () => {
      window.removeEventListener('resize', sendHeightUpdate);
      observer.disconnect();
    };
  }, []);

  return (
    <SignUpMain className="min-h-0">
      {enableQuestionnaire && displayQuestionnaire && (
        <Questionnaire onSubmit={onQuestionnaireSubmit} loading={isLoading} />
      )}
      {(!enableQuestionnaire || !displayQuestionnaire) && (
        <SignUpSection>
          <SignUpSectionContainer>
            <SignUpFormContainer>
              <SignUpForm
                canContinueGoogle={canContinueGoogle}
                onSuccessfulGoogleValidation={onSuccessfulGoogleValidation}
                questionnaireData={questionnaireData}
                onSubmit={onSubmit}
                signUpError={signUpError}
                isLoading={isLoading}
              />
            </SignUpFormContainer>
          </SignUpSectionContainer>
        </SignUpSection>
      )}
    </SignUpMain>
  );
}

function SignUpStandard({
  canContinueGoogle,
  onSuccessfulGoogleValidation,
  questionnaireData,
  onSubmit,
  signUpError,
  isLoading,
  enableQuestionnaire,
  displayQuestionnaire,
  onQuestionnaireSubmit
}: SignUpProps) {
  const [t, { language }] = useTranslation();

  return (
    <SignUpMain className="flex-col md:flex-row">
      <div className="flex flex-1 md:flex-col">
        <SignUpHeader />
        {enableQuestionnaire && displayQuestionnaire && (
          <Questionnaire onSubmit={onQuestionnaireSubmit} loading={isLoading} />
        )}
        {(!enableQuestionnaire || !displayQuestionnaire) && (
          <SignUpSection>
            <SignUpSectionContainer>
              <SignUpFormContainer>
                <SignUpFormIntro />
                <SignUpForm
                  canContinueGoogle={canContinueGoogle}
                  onSuccessfulGoogleValidation={onSuccessfulGoogleValidation}
                  questionnaireData={questionnaireData}
                  onSubmit={onSubmit}
                  signUpError={signUpError}
                  isLoading={isLoading}
                />
                <TermsAndPrivacy />
              </SignUpFormContainer>
            </SignUpSectionContainer>
          </SignUpSection>
        )}
      </div>
      {(!enableQuestionnaire || !displayQuestionnaire) && <SignUpRightSection />}
      <Helmet>
        <title lang={language}>{`${t('components.sign_up.title')} • ${t(
          'titles.app_name'
        )}`}</title>
      </Helmet>
    </SignUpMain>
  );
}

export function SignUp() {
  const [t] = useTranslation();
  const [signUpError, setSignUpError] = useState('');
  const signUp = useSignUpMutation();
  const validateSignUp = useValidateSignUpMutation();
  const [displayQuestionnaire, setDisplayQuestionnaire] = useState(false);
  const [userCredentials, setUserCredentials] = useState<Credentials>();
  const [canContinueGoogle, setCanContinueGoogle] = useState(false);
  const [isGoogleSignUp, setIsGoogleSignUp] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData>();

  const enableQuestionnaire = false; // GT-207 Temporarily disable questionnaire

  const { search } = useLocation();
  const [searchParams] = useSearchParams();

  const embedSearchParam = searchParams.get('embed');
  const enableEmbeddedForm = embedSearchParam === 'true';

  useEffect(() => {
    const params = new URLSearchParams(search);
    const referralCookie = Cookies.get('referral');

    if (window.analytics) {
      // Assign Google Analytics tracking properties to Cookies to be captured by the Server
      getGAnalyticsProperties().forEach(
        ({ key, value }) => value !== undefined && Cookies.set(key, value, { expires: 30 })
      );

      if (
        referralCookie ||
        !document.referrer.includes('dashboard.knack.com') ||
        params.get('referral') ||
        params.get('ref')
      ) {
        window.analytics.track('Sign Up Referral', {
          referral: getReferral()
        });
      }
    }
  }, [search]);

  /**
   * Check if there is a Google Ad cookie from the Website page and if so, add the gclid and clientid to the credentials
   * @param credentials The info from the Sign-up form. Includes username and password
   * @returns
   */
  const checkGoogleCookie = (credentials: Credentials) => {
    const fromGoogleAd = Cookies.get('kn_ad');
    if (fromGoogleAd) {
      const knAdCookieParts = fromGoogleAd.split('~');
      return {
        ...credentials,
        gclid: knAdCookieParts[0],
        clientid: knAdCookieParts[1]
      };
    }
    return credentials;
  };

  /**
   * Attempts to create a new user with the data in the user object. Will generate an error if
   * the email already exists, if we fail to generate a unique account slug, or if server-side
   * validation of email/password somehow fail.
   */
  const submitSignUp = async (credentials: Credentials, questionnaire?: QuestionnaireData) => {
    if (!credentials) {
      setSignUpError(t('errors.generic_error'));
      return;
    }

    await signUp.mutateAsync(
      { credentials, questionnaire },
      {
        onSuccess: async (session) => {
          if (shouldSendAnalytics(session.user.email)) {
            pushToDataLayer('signup-email');
          }
        },
        onError: (err: any) => {
          const errorCode = err.response?.data?.errors[0]?.message;

          const errorMessage =
            !errorCode || errorCode === 'generic_error'
              ? 'errors.generic_error'
              : `components.auth.${errorCode}`;

          setSignUpError(t(errorMessage));
        },
        onSettled: () => {
          setDisplayQuestionnaire(false);
        }
      }
    );
  };

  /**
   * Submits the onboarding questionnaire data. It checks for valid credentials before submission.
   * If credentials are invalid, it displays an error and hides the questionnaire. During submission,
   * it manages loading states and sends analytics data based on email eligibility. After submission,
   * it resets the UI state.
   *
   * @param {QuestionnaireData} data - Data from the onboarding questionnaire.
   */
  const onQuestionnaireSubmit = async (data: QuestionnaireData) => {
    setQuestionnaireData(data);

    if (userCredentials) {
      await submitSignUp(userCredentials, data);
    } else if (isGoogleSignUp) {
      // The `isGoogleSignUp` flag is just to make a distinction in missing userCredentials and being
      // on the Google flow. Without this flag this would be an "else" statement and that's not desired
      // as having an unset userCredentials shouldn't directly mean that the user is on the Google flow.
      setCanContinueGoogle(true);

      // We can already unset this flag as if the GoogleButton receives new credentials this would get set
      // as true. This avoids accidentally storing flags that don't belong to the same set of credentials.
      setIsGoogleSignUp(false);
    } else {
      setSignUpError(t('errors.generic_error'));
      setDisplayQuestionnaire(false);
    }
  };

  /**
   * Validates the request credentials, in case we have enabled the onboarding questionnaire in
   * it will display this questionnaire after a successful validation, when not enabled
   * it will call submitSignUp function directly.
   */
  const onSubmit = async (data: Credentials) => {
    const credentials = checkGoogleCookie(data);
    setUserCredentials(credentials);

    await validateSignUp.mutateAsync(credentials, {
      onSuccess: async () => {
        if (enableQuestionnaire) {
          setDisplayQuestionnaire(true);
          return;
        }
        await submitSignUp(credentials);
      },
      onError: (err: any) => {
        const errorCode = err.response?.data?.errors[0]?.message;

        const errorMessage =
          !errorCode || errorCode === 'generic_error'
            ? 'errors.generic_error'
            : `components.auth.${errorCode}`;

        setSignUpError(t(errorMessage));
      }
    });
  };

  const onSuccessfulGoogleValidation = (userExists: boolean) => {
    // We allow the GoogleButton to continue with its flow if the user already exists (because
    // it's a sign-in). If not, it has to fill up the questionnaire first if enabled.
    setCanContinueGoogle(enableQuestionnaire ? userExists : true);

    if (!userExists) {
      setIsGoogleSignUp(true);
      setDisplayQuestionnaire(enableQuestionnaire);
    }
  };

  if (enableEmbeddedForm) {
    return (
      <SignUpEmbedded
        canContinueGoogle={canContinueGoogle}
        onSuccessfulGoogleValidation={onSuccessfulGoogleValidation}
        questionnaireData={questionnaireData}
        onSubmit={onSubmit}
        signUpError={signUpError}
        isLoading={signUp.isPending}
        enableQuestionnaire={enableQuestionnaire}
        displayQuestionnaire={displayQuestionnaire}
        onQuestionnaireSubmit={onQuestionnaireSubmit}
      />
    );
  }

  return (
    <SignUpStandard
      canContinueGoogle={canContinueGoogle}
      onSuccessfulGoogleValidation={onSuccessfulGoogleValidation}
      questionnaireData={questionnaireData}
      onSubmit={onSubmit}
      signUpError={signUpError}
      isLoading={signUp.isPending}
      displayQuestionnaire={displayQuestionnaire}
      onQuestionnaireSubmit={onQuestionnaireSubmit}
      enableQuestionnaire={enableQuestionnaire}
    />
  );
}

export function SignUpIFrameFallback() {
  const [t] = useTranslation();
  const [searchParams] = useSearchParams();

  const searchParamsSerialized = searchParams.toString();
  const urlSearchParams = searchParamsSerialized ? `?${searchParamsSerialized}` : '';

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <p>{t('components.account.already_signed')}</p>
      <Button
        className="block"
        onClick={() => {
          window.open(`${import.meta.env.PUBLIC_DASHBOARD_URL}${urlSearchParams}`, '_blank');
        }}
      >
        {t('components.account.go_to_dashboard')}
      </Button>
    </div>
  );
}
